// @ts-nocheck
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, EyeOff, Lock, RefreshCw } from 'lucide-react';
import { useCompanyContext } from '../../../context/CompanyContext';
import { buildVoteRoomDqisFromFormData } from '../../../utils/build280gAnalysisResults';
import { launchAnalysisVoteRoom, getLastAnalysisRun } from '../../../../../services/AnalysisApiClient';
import { pushToast } from '../../../../../utils/toast/toastStore';
import { summarizeVoteRoomLaunchSend, voteRoomLaunchSendFailed } from './shared/voteRoomLaunchSend';
import { getVoteTally } from '../../../../../services/ApprovalEnvelopeApiClient';
import { StatusBadge } from './shared/StatusBadge';
import { applyTallyToCapTable, buildLiveCapTableFromApiRows, loadShareholdersOnce } from './shared/voteRoomMapping';
import { DEFAULT_SHUTDOWN_SETTINGS, shutdownSettingsFromAnalysis } from './shared/voteRoomShutdown';
import { isStrictThresholdMet, sumSharesByStatus } from './shared/voteTallyMath';
import { SetupPage } from './setup/SetupPage';
import { AdminDashboard } from './live/AdminDashboard';
import { ShutdownSettingsSlideOver } from './live/ShutdownSettingsSlideOver';
import { ViewActivitySlideOver } from './live/ViewActivitySlideOver';
import { PreviewVoteRoomSlideOver } from './live/PreviewVoteRoomSlideOver';

/**
 * Top-level Vote Room experience: hosts the setup wizard (pre-launch) or the live admin dashboard
 * (post-launch), the shutdown settings + slide-overs, and the "highly sensitive" chrome banner.
 */
export function VoteRoom280FullExperience({ formData }) {
  const { currentAnalysisId, currentAnalysis, setAnalyses } = useCompanyContext();
  const voteRoomLaunchedAt = currentAnalysis?.voteRoomLaunchedAt ?? null;
  const isLocked = !!voteRoomLaunchedAt;

  const [previewShareholder, setPreviewShareholder] = useState(null);
  const [shareholders, setShareholders] = useState([]);
  const [eligibleShares, setEligibleShares] = useState(0);
  const [excludedDqis, setExcludedDqis] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showActivity, setShowActivity] = useState(null);
  const [shutdown, setShutdown] = useState(DEFAULT_SHUTDOWN_SETTINGS);
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState(null);
  const [liveLoading, setLiveLoading] = useState(isLocked);
  const [liveLoadError, setLiveLoadError] = useState(null);
  const [tallyLoadWarning, setTallyLoadWarning] = useState(null);
  const [shareholderSendWarning, setShareholderSendWarning] = useState(null);
  const [retryingShareholderSend, setRetryingShareholderSend] = useState(false);

  const companyName = String(formData?.companyName || '').trim() || 'Company';
  const dqis = useMemo(() => buildVoteRoomDqisFromFormData(formData), [formData]);

  const goLive = (eligible, elig, excluded) => {
    setShareholders(eligible);
    setEligibleShares(elig);
    setExcludedDqis(excluded);
    setLiveLoading(false);
    setLiveLoadError(null);
  };

  // Loads the live cap table and overlays the server vote tally so per-shareholder status and the
  // approval progress reflect real votes (recorded via DocuSign webhook / manual override).
  const reloadLive = useCallback(
    async (signal) => {
      if (!currentAnalysisId) return;
      let runId = currentAnalysis?.lastRunId ?? null;
      if (!runId) {
        try {
          const last = await getLastAnalysisRun(currentAnalysisId);
          runId = last?.runId ?? null;
        } catch {
          runId = null;
        }
      }
      const [rows, tally] = await Promise.all([
        loadShareholdersOnce(currentAnalysisId),
        runId
          ? getVoteTally(currentAnalysisId, runId).catch(() => {
              if (!signal?.cancelled) {
                setTallyLoadWarning('Live vote status could not be loaded; showing cap table only.');
              }
              return null;
            })
          : Promise.resolve(null),
      ]);
      if (signal?.cancelled) return;
      if (tally) setTallyLoadWarning(null);
      const { prepared, eligShares, excluded } = applyTallyToCapTable(buildLiveCapTableFromApiRows(rows), tally);
      goLive(prepared, eligShares, excluded);
    },
    [currentAnalysisId, currentAnalysis?.lastRunId]
  );

  useEffect(() => {
    if (!isLocked || !currentAnalysisId) {
      setLiveLoading(false);
      return undefined;
    }

    const signal = { cancelled: false };
    setLiveLoading(true);
    setLiveLoadError(null);

    reloadLive(signal).catch((error) => {
      if (!signal.cancelled) {
        setLiveLoadError(error instanceof Error ? error.message : 'Failed to load shareholders.');
        setLiveLoading(false);
      }
    });

    return () => {
      signal.cancelled = true;
    };
  }, [isLocked, currentAnalysisId, reloadLive]);

  const retryShareholderDocumentSend = useCallback(async () => {
    if (!currentAnalysisId || retryingShareholderSend) return;
    setRetryingShareholderSend(true);
    setShareholderSendWarning(null);
    try {
      const { voteRoomLaunchSend } = await launchAnalysisVoteRoom(currentAnalysisId, {
        shutdownMode: shutdown.mode,
        shutdownCustomThreshold: shutdown.mode === 'threshold_custom' ? shutdown.customThreshold : null,
        shutdownHours: shutdown.mode === 'time_based' ? shutdown.hours : null,
      });
      const summary = summarizeVoteRoomLaunchSend(voteRoomLaunchSend);
      if (voteRoomLaunchSendFailed(voteRoomLaunchSend)) {
        setShareholderSendWarning(summary?.message ?? 'Shareholder documents could not be sent.');
        if (summary) pushToast(summary.message, summary.variant);
        return;
      }
      if (summary) {
        pushToast(summary.message, summary.variant === 'success' ? 'success' : summary.variant);
      }
      await reloadLive().catch((e) => console.warn('Live reload after shareholder send retry failed.', e));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send shareholder documents.';
      setShareholderSendWarning(message);
      pushToast(message, 'error');
    } finally {
      setRetryingShareholderSend(false);
    }
  }, [currentAnalysisId, retryingShareholderSend, shutdown, reloadLive]);

  useEffect(() => {
    if (!isLocked || !currentAnalysisId) {
      setShareholderSendWarning(null);
      return undefined;
    }

    const signal = { cancelled: false };
    launchAnalysisVoteRoom(currentAnalysisId, {
      shutdownMode: shutdown.mode,
      shutdownCustomThreshold: shutdown.mode === 'threshold_custom' ? shutdown.customThreshold : null,
      shutdownHours: shutdown.mode === 'time_based' ? shutdown.hours : null,
    })
      .then(({ voteRoomLaunchSend }) => {
        if (signal.cancelled) return;
        if (voteRoomLaunchSendFailed(voteRoomLaunchSend)) {
          const summary = summarizeVoteRoomLaunchSend(voteRoomLaunchSend);
          setShareholderSendWarning(
            summary?.message ?? 'Shareholder documents were not sent. Try again or contact support.'
          );
        } else {
          setShareholderSendWarning(null);
        }
      })
      .catch(() => {
        if (!signal.cancelled) {
          setShareholderSendWarning(null);
        }
      });

    return () => {
      signal.cancelled = true;
    };
  }, [isLocked, currentAnalysisId, shutdown.mode, shutdown.customThreshold, shutdown.hours]);

  useEffect(() => {
    setShutdown(shutdownSettingsFromAnalysis(currentAnalysis));
  }, [
    currentAnalysis?.id,
    currentAnalysis?.voteRoomShutdownMode,
    currentAnalysis?.voteRoomShutdownCustomThreshold,
    currentAnalysis?.voteRoomShutdownHours,
  ]);

  const handleLaunch = async (eligible, elig, excluded) => {
    setLaunchError(null);
    if (isLocked || !currentAnalysisId) {
      goLive(eligible, elig, excluded);
      return;
    }
    setLaunching(true);
    try {
      const { analysis: updated, voteRoomLaunchSend } = await launchAnalysisVoteRoom(currentAnalysisId, {
        shutdownMode: shutdown.mode,
        shutdownCustomThreshold: shutdown.mode === 'threshold_custom' ? shutdown.customThreshold : null,
        shutdownHours: shutdown.mode === 'time_based' ? shutdown.hours : null,
      });
      if (!updated?.voteRoomLaunchedAt) {
        setLaunchError('Failed to launch Vote Room.');
        return;
      }
      if (voteRoomLaunchSendFailed(voteRoomLaunchSend)) {
        const summary = summarizeVoteRoomLaunchSend(voteRoomLaunchSend);
        setLaunchError(summary?.message ?? 'Shareholder documents could not be sent.');
        if (summary) pushToast(summary.message, summary.variant);
        return;
      }
      if (setAnalyses) {
        setAnalyses((prev) =>
          prev.map((a) =>
            a.id === currentAnalysisId
              ? {
                  ...a,
                  voteRoomLaunchedAt: updated.voteRoomLaunchedAt ?? a.voteRoomLaunchedAt,
                  voteRoomShutdownMode: updated.voteRoomShutdownMode ?? a.voteRoomShutdownMode,
                  voteRoomShutdownCustomThreshold:
                    updated.voteRoomShutdownCustomThreshold ?? a.voteRoomShutdownCustomThreshold,
                  voteRoomShutdownHours: updated.voteRoomShutdownHours ?? a.voteRoomShutdownHours,
                  status: updated.status ?? a.status,
                }
              : a
          )
        );
      }
      if (updated) {
        setShutdown(shutdownSettingsFromAnalysis(updated));
      }
      const sendSummary = summarizeVoteRoomLaunchSend(voteRoomLaunchSend);
      if (sendSummary) {
        pushToast(
          sendSummary.variant === 'success' ? `Vote Room launched. ${sendSummary.message}` : sendSummary.message,
          sendSummary.variant
        );
      } else {
        pushToast('Vote Room launched.', 'success');
      }
      setShareholderSendWarning(null);
      goLive(eligible, elig, excluded);
    } catch (error) {
      setLaunchError(error instanceof Error ? error.message : 'Failed to launch Vote Room.');
    } finally {
      setLaunching(false);
    }
  };

  // Preview-only: signing in the admin slide-over does not persist votes.
  const eff = shutdown.mode === 'threshold_custom' ? shutdown.customThreshold / 100 : 0.75;
  const approvedShares = sumSharesByStatus(shareholders, 'approved');
  const closed = shutdown.mode !== 'time_based' && isStrictThresholdMet(approvedShares, eligibleShares, eff);

  const showLiveLoading = isLocked && liveLoading && shareholders.length === 0;
  const showLiveError = isLocked && liveLoadError && shareholders.length === 0;
  const showLiveDashboard = isLocked && !showLiveLoading && !showLiveError;

  return (
    <div
      className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
      style={{ fontFamily: "'Inter',-apple-system,sans-serif" }}
    >
      <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-emerald-100 bg-emerald-50/60 px-5 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <EyeOff size={14} className="flex-shrink-0 text-emerald-700" />
          <p className="truncate text-xs text-emerald-900">
            <span className="font-semibold">Highly Sensitive.</span>{' '}
            <span className="text-emerald-800/90">
              Material nonpublic information — do not share, screenshot, or discuss outside authorized personnel.
            </span>
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          {closed && (
            <StatusBadge variant="closed">
              <Lock size={8} className="mr-1" />
              Closed
            </StatusBadge>
          )}
          <StatusBadge variant="secure">
            <Lock size={8} className="mr-1" />
            Encrypted
          </StatusBadge>
        </div>
      </div>
      <div className="flex-1 p-6">
        <div className="w-full">
          {!isLocked && (
            <SetupPage
              onLaunch={handleLaunch}
              dqis={dqis}
              companyName={companyName}
              analysisId={currentAnalysisId}
              locked={isLocked}
              launching={launching}
              launchError={launchError}
              shutdownSettings={shutdown}
              onShutdownChange={setShutdown}
              onShutdownSaved={(updated) => {
                if (setAnalyses && currentAnalysisId) {
                  setAnalyses((prev) =>
                    prev.map((a) =>
                      a.id === currentAnalysisId
                        ? {
                            ...a,
                            voteRoomShutdownMode: updated.voteRoomShutdownMode ?? a.voteRoomShutdownMode,
                            voteRoomShutdownCustomThreshold:
                              updated.voteRoomShutdownCustomThreshold ?? a.voteRoomShutdownCustomThreshold,
                            voteRoomShutdownHours: updated.voteRoomShutdownHours ?? a.voteRoomShutdownHours,
                          }
                        : a
                    )
                  );
                }
                setShutdown(shutdownSettingsFromAnalysis(updated));
              }}
            />
          )}
          {showLiveLoading && (
            <div className="flex items-center justify-center py-16 text-xs text-gray-500">
              <RefreshCw size={16} className="mr-2 animate-spin text-emerald-600" />
              Loading shareholders…
            </div>
          )}
          {showLiveError && (
            <div className="bg-red-50 border border-red-100 rounded-lg px-4 py-3 text-xs text-red-700 flex items-start gap-2">
              <AlertTriangle size={13} className="mt-0.5 flex-shrink-0 text-red-400" />
              <span>{liveLoadError}</span>
            </div>
          )}
          {showLiveDashboard && (
            <>
              {tallyLoadWarning ? (
                <div className="mb-4 bg-amber-50 border border-amber-100 rounded-lg px-4 py-2.5 text-xs text-amber-800">
                  {tallyLoadWarning}
                </div>
              ) : null}
              {shareholderSendWarning ? (
                <div className="mb-4 bg-red-50 border border-red-100 rounded-lg px-4 py-3 text-xs text-red-800 flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2 min-w-0">
                    <AlertTriangle size={13} className="mt-0.5 flex-shrink-0 text-red-500" />
                    <span>{shareholderSendWarning}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => void retryShareholderDocumentSend()}
                    disabled={retryingShareholderSend}
                    className="inline-flex items-center px-2.5 py-1.5 rounded-md text-[11px] font-semibold bg-white text-red-800 border border-red-200 hover:bg-red-50 disabled:opacity-40 flex-shrink-0"
                  >
                    {retryingShareholderSend ? <RefreshCw size={12} className="mr-1 animate-spin" /> : null}
                    {retryingShareholderSend ? 'Sending…' : 'Retry Send'}
                  </button>
                </div>
              ) : null}
              <AdminDashboard
                shareholders={shareholders}
                eligibleShares={eligibleShares}
                excludedDqis={excludedDqis}
                shutdownSettings={shutdown}
                onViewAs={(sh) => setPreviewShareholder(sh)}
                onOpenSettings={() => setShowSettings(true)}
                onOpenViewActivity={(sh) => setShowActivity(sh)}
                companyName={companyName}
              />
            </>
          )}
        </div>
      </div>
      <ShutdownSettingsSlideOver
        open={showSettings}
        settings={shutdown}
        onSave={setShutdown}
        onClose={() => setShowSettings(false)}
        readOnly={isLocked}
      />
      <ViewActivitySlideOver
        shareholder={showActivity}
        shareholders={shareholders}
        eligibleShares={eligibleShares}
        voteRoomClosed={closed}
        onClose={() => setShowActivity(null)}
      />
      <PreviewVoteRoomSlideOver
        shareholder={previewShareholder}
        shareholders={shareholders}
        eligibleShares={eligibleShares}
        onClose={() => setPreviewShareholder(null)}
        voteRoomClosed={closed}
        companyName={companyName}
      />
    </div>
  );
}
